import React, { memo, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import ReactDOM from 'react-dom';
/**
 * Throw error when ModalContext is used outside of context provider
 */

var invariantViolation = function invariantViolation() {
  throw new Error("Attempted to call useModal outside of modal context. Make sure your app is rendered inside ModalProvider.");
};
/**
 * Modal Context Object
 */


var ModalContext = /*#__PURE__*/React.createContext({
  showModal: invariantViolation,
  hideModal: invariantViolation
});
/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0) t[p[i]] = s[p[i]];
  }
  return t;
}
/**
 * Component responsible for rendering the modal.
 *
 * The identity of `Component` may change dependeing on the inputs passed to
 * `useModal`. If we simply rendered `<Component />` then the modal would be
 * susceptible to rerenders whenever one of the inputs change.
 */


var ModalRenderer = /*#__PURE__*/memo(function (_a) {
  var component = _a.component,
      rest = __rest(_a, ["component"]);

  return component(rest);
});
/**
 * Modal Root
 *
 * Renders modals using react portal.
 */

var ModalRoot = /*#__PURE__*/memo(function (_a) {
  var modals = _a.modals,
      container = _a.container,
      _b = _a.component,
      RootComponent = _b === void 0 ? React.Fragment : _b;

  var _c = useState(undefined),
      mountNode = _c[0],
      setMountNode = _c[1]; // This effect will not be ran in the server environment


  useEffect(function () {
    return setMountNode(container || document.body);
  });
  return mountNode ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(RootComponent, null, Object.keys(modals).map(function (key) {
    return /*#__PURE__*/React.createElement(ModalRenderer, {
      key: key,
      component: modals[key]
    });
  })), mountNode) : null;
});
/**
 * Modal Provider
 *
 * Provides modal context and renders ModalRoot.
 */

var ModalProvider = function ModalProvider(_a) {
  var container = _a.container,
      rootComponent = _a.rootComponent,
      children = _a.children;

  if (container && !(container instanceof HTMLElement)) {
    throw new Error("Container must specify DOM element to mount modal root into.\n    \n    This behavior has changed in 3.0.0. Please use `rootComponent` prop instead.\n    See: https://github.com/mpontus/react-modal-hook/issues/18");
  }

  var _b = useState({}),
      modals = _b[0],
      setModals = _b[1];

  var showModal = useCallback(function (key, modal) {
    return setModals(function (modals) {
      var _a;

      return _assign({}, modals, (_a = {}, _a[key] = modal, _a));
    });
  }, []);
  var hideModal = useCallback(function (key) {
    return setModals(function (modals) {
      var newModals = _assign({}, modals);

      delete newModals[key];
      return newModals;
    });
  }, []);
  var contextValue = useMemo(function () {
    return {
      showModal: showModal,
      hideModal: hideModal
    };
  }, []);
  return /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(React.Fragment, null, children, /*#__PURE__*/React.createElement(ModalRoot, {
    modals: modals,
    component: rootComponent,
    container: container
  })));
};
/**
 * Utility function to generate unique number per component instance
 */


var generateModalKey = function () {
  var count = 0;
  return function () {
    return "" + ++count;
  };
}();
/**
 * Check whether the argument is a stateless component.
 *
 * We take advantage of the stateless nature of functional components to be
 * inline the rendering of the modal component as part of another immutable
 * component.
 *
 * This is necessary for allowing the modal to update based on the inputs passed
 * as the second argument to useModal without unmounting the previous version of
 * the modal component.
 */


var isFunctionalComponent = function isFunctionalComponent(Component) {
  var prototype = Component.prototype;
  return !prototype || !prototype.isReactComponent;
};
/**
 * React hook for showing modal windows
 */


var useModal = function useModal(component, inputs) {
  if (inputs === void 0) {
    inputs = [];
  }

  if (!isFunctionalComponent(component)) {
    throw new Error("Only stateless components can be used as an argument to useModal. You have probably passed a class component where a function was expected.");
  }

  var key = useMemo(generateModalKey, []);
  var modal = useMemo(function () {
    return component;
  }, inputs);
  var context = useContext(ModalContext);

  var _a = useState(false),
      isShown = _a[0],
      setShown = _a[1];

  var showModal = useCallback(function () {
    return setShown(true);
  }, []);
  var hideModal = useCallback(function () {
    return setShown(false);
  }, []);
  useEffect(function () {
    if (isShown) {
      context.showModal(key, modal);
    } else {
      context.hideModal(key);
    } // Hide modal when parent component unmounts


    return function () {
      return context.hideModal(key);
    };
  }, [modal, isShown]);
  return [showModal, hideModal];
};

export { ModalContext, ModalProvider, useModal };